import type { WorkbenchMenuItem } from "./workbench-menu-types.js";

type MenuItemOptions = {
  enabled?: boolean;
  label?: string;
  run?: () => void;
  shortcut?: string;
};

function normalizeLabel(text: string | null | undefined, fallback: string): string {
  const normalized = text?.replace(/\s+/g, " ").trim() ?? "";
  return normalized === "" ? fallback : normalized;
}

function dispatchChange(target: HTMLInputElement | HTMLSelectElement): void {
  target.dispatchEvent(new Event("change", { bubbles: true }));
}

export function menuSeparator(): WorkbenchMenuItem {
  return { type: "separator" };
}

export function buttonMenuItem(id: string, button: HTMLButtonElement, options?: MenuItemOptions): WorkbenchMenuItem {
  return {
    type: "action",
    id,
    label: normalizeLabel(options?.label ?? button.textContent, id),
    enabled: options?.enabled ?? !button.disabled,
    shortcut: options?.shortcut,
    run() {
      if (options?.run !== undefined) {
        options.run();
        return;
      }
      if (button.disabled) {
        return;
      }
      button.click();
    }
  };
}

export function actionMenuItem(
  id: string,
  label: string,
  enabled: boolean,
  run: () => void,
  shortcut?: string
): WorkbenchMenuItem {
  return {
    type: "action",
    id,
    label,
    enabled,
    shortcut,
    run
  };
}

export function checkboxMenuItem(
  id: string,
  label: string,
  input: HTMLInputElement,
  options?: Omit<MenuItemOptions, "label">
): WorkbenchMenuItem {
  return {
    type: "checkbox",
    id,
    label,
    checked: input.checked,
    enabled: options?.enabled ?? !input.disabled,
    shortcut: options?.shortcut,
    run() {
      if (options?.run !== undefined) {
        options.run();
        return;
      }
      if (input.disabled) {
        return;
      }
      input.click();
    }
  };
}

export function radioMenuItem(
  id: string,
  label: string,
  checked: boolean,
  enabled: boolean,
  run: () => void,
  shortcut?: string
): WorkbenchMenuItem {
  return {
    type: "radio",
    id,
    label,
    checked,
    enabled,
    shortcut,
    run
  };
}

export function selectOptionMenuItem(
  id: string,
  select: HTMLSelectElement,
  value: string,
  options?: Omit<MenuItemOptions, "enabled">
): WorkbenchMenuItem {
  const option = Array.from(select.options).find((entry) => entry.value === value);
  const label = normalizeLabel(options?.label ?? option?.textContent, value);
  return {
    type: "radio",
    id,
    label,
    checked: select.value === value,
    enabled: !select.disabled,
    shortcut: options?.shortcut,
    run() {
      if (options?.run !== undefined) {
        options.run();
        return;
      }
      if (select.disabled) {
        return;
      }
      select.value = value;
      dispatchChange(select);
    }
  };
}
